import type { ReactElement } from "react";
import { cn } from "../../lib/utils.js";
import { statusToken } from "../../lib/types.js";
import { statusDeltaClass } from "../kpi-spark-tone.js";
import type { WidgetBodyProps } from "../types.js";
import { MAX_WATCHLIST_KPIS } from "./constants.js";
import type { KpiWatchlistView } from "./types.js";

/**
 * KPI Watchlist — dashboard data widget. A cross-plant signal feed: up to six
 * KPIs as a status dot + name + value + delta. Data-blind: the host resolves the
 * pinned ids to `KpiDef`s. Membership is edited via the dialog's checklist editor,
 * so rows are display-only (no per-row pin button — see plan §6/§12).
 */
export function KpiWatchlistWidget({
  data,
}: WidgetBodyProps<KpiWatchlistView>): ReactElement {
  const rows = data.kpis.slice(0, MAX_WATCHLIST_KPIS);

  if (rows.length === 0) {
    return (
      <p className="text-dense-1 text-pb-text-muted italic">
        No KPIs in this watchlist — add some from the edit dialog.
      </p>
    );
  }

  return (
    <ul className="space-y-1">
      {rows.map((k) => (
        <li
          key={k.id}
          className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-sm transition-colors hover:pb-overlay-paint"
        >
          <span className="flex items-center gap-2 min-w-0">
            <span
              className={cn(
                "w-2 h-2 rounded-full shrink-0",
                statusToken(k.status).dotClass,
              )}
            />
            <span className="text-sm text-pb-text truncate">{k.name}</span>
          </span>
          <span className="flex items-center gap-3 shrink-0">
            <span className="text-sm text-pb-text font-mono pb-tabular">
              {k.value}
            </span>
            <span
              className={cn(
                "text-xs font-mono pb-tabular w-12 text-end",
                statusDeltaClass(k.status),
              )}
            >
              {k.delta}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}
